/**
 * Waive a requirement and lift a waiver (plan docs/plans/lms-plan.md 8.4,
 * 5.4, 18.16; R22, G9; WP-45). Used from a matrix cell and from the person
 * page's Assignments and Waivers tabs.
 *
 * Waive (POST /admin/assignments/:id/waive {reason}): the open assignment
 * becomes waived, with the reason (required, free text up to 500 characters,
 * with a picker of the common reasons 8.4 names that fills the text in) and
 * the acting admin recorded; the learner is told in-app (9.2 'waived').
 *
 * Lift (POST /admin/assignments/:id/unwaive {due_at?}): "a waiver is undone by
 * a new assignment" (8.4). The waived row stays as history and a new open
 * assignment is created, due on the chosen day (the end of that local day) or,
 * when no day is chosen, in the network's default_due_days (18.16).
 *
 * Both are forms without their own dialog frame, so a caller puts them inside
 * the Modal it already has open (the matrix's cell dialog) or in WaiverDialogs.
 */
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { chipCls, fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { useToast } from '../../../../ui/Toast';
import { apiErrorMessage } from '../../../api/client';
import { ASSIGNMENT_REASON_MAX, unwaiveAssignment, waiveAssignment } from '../../../api/assignments';
import type { LmsAssignment } from '../../../api/assignments';
import { toDayString } from '../../../api/reports';
import { ErrorNote, formatDay, labelCls, metaCls } from '../../../components/pickers/shared';
import { endOfLocalDayIso } from './shared';

/** 8.4: "a picker of common reasons: equivalent external training accepted, role does not require, on leave". */
export const COMMON_WAIVE_REASONS: readonly string[] = [
  "Equivalent external training accepted",
  "Role does not require this course",
  "On leave",
];

export interface WaiverFormProps {
  assignmentId: string;
  /** The person and the course, for the confirmation line. */
  personName: string;
  courseTitle: string;
  onCancel: () => void;
  onDone: (assignment: LmsAssignment) => void;
}

export function WaiveForm({ assignmentId, personName, courseTitle, onCancel, onDone }: WaiverFormProps) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const text = reason.trim();
  const tooLong = reason.length > ASSIGNMENT_REASON_MAX;

  const submit = async () => {
    if (!text || tooLong || busy) return;
    setBusy(true);
    setError(null);
    try {
      const waived = await waiveAssignment(assignmentId, text);
      toast(`Waived ${courseTitle} for ${personName}.`, { type: 'success' });
      onDone(waived);
    } catch (err) {
      setError(apiErrorMessage(err, 'The waiver could not be recorded. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-3"
      data-testid="waive-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="text-sm text-gray-700 dark:text-gray-200">
        {`${personName} will no longer need to complete ${courseTitle}. The waiver and its reason are recorded, and the learner is told.`}
      </p>
      <div>
        <span className={labelCls}>Common reasons</span>
        <div className="flex flex-wrap gap-1.5">
          {COMMON_WAIVE_REASONS.map((r, i) => (
            <button
              key={r}
              type="button"
              className={chipCls(reason === r)}
              aria-pressed={reason === r}
              onClick={() => setReason(r)}
              data-testid={`waive-reason-${i}`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor={`waive-reason-${assignmentId}`}>
          Reason (required)
        </label>
        <textarea
          id={`waive-reason-${assignmentId}`}
          className={`${fieldCls} w-full`}
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="waive-reason-text"
          aria-invalid={tooLong}
        />
        <p className={metaCls}>
          {tooLong ? `At most ${ASSIGNMENT_REASON_MAX} characters.` : `${reason.length}/${ASSIGNMENT_REASON_MAX}`}
        </p>
      </div>
      {error ? <ErrorNote testId="waive-error">{error}</ErrorNote> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={secondaryBtnCls} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className={primaryBtnCls} disabled={!text || tooLong || busy} data-testid="waive-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          Waive
        </button>
      </div>
    </form>
  );
}

export function UnwaiveForm({ assignmentId, personName, courseTitle, onCancel, onDone }: WaiverFormProps) {
  const { toast } = useToast();
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dueAt = day ? endOfLocalDayIso(day) : null;
  const badDay = day !== '' && dueAt === null;

  const submit = async () => {
    if (busy || badDay) return;
    setBusy(true);
    setError(null);
    try {
      const open = await unwaiveAssignment(assignmentId, dueAt ? { due_at: dueAt } : {});
      toast(`Waiver lifted: ${courseTitle} is assigned to ${personName} again, due ${formatDay(open.due_at)}.`, { type: 'success' });
      onDone(open);
    } catch (err) {
      setError(apiErrorMessage(err, 'The waiver could not be lifted. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-3"
      data-testid="unwaive-form"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="text-sm text-gray-700 dark:text-gray-200">
        {`Lifting the waiver assigns ${courseTitle} to ${personName} again. The waiver stays on the record as history.`}
      </p>
      <div>
        <label className={labelCls} htmlFor={`unwaive-due-${assignmentId}`}>
          New due date (optional)
        </label>
        <input
          id={`unwaive-due-${assignmentId}`}
          type="date"
          className={`${fieldCls} w-full sm:w-auto`}
          value={day}
          min={toDayString(new Date())}
          onChange={(e) => setDay(e.target.value)}
          data-testid="unwaive-due"
        />
        <p className={metaCls}>{"Left empty, it is due after the network's default due window."}</p>
      </div>
      {error ? <ErrorNote testId="unwaive-error">{error}</ErrorNote> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={secondaryBtnCls} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className={primaryBtnCls} disabled={busy || badDay} data-testid="unwaive-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          Lift waiver
        </button>
      </div>
    </form>
  );
}
